import {
  useState,
  useEffect,
  useCallback,
  useRef,
  memo,
  type FC,
  type ReactNode
} from 'react';
import { Document as PdfDocument, Page, pdfjs } from 'react-pdf';
import { IconLayoutList, IconLayoutNavbar } from '@tabler/icons-react';
import { BACKEND_URL } from '../../constants/apollo';
import { AUTH_KEY } from '../../constants/storageKeys';
import { useDownloadContent } from '../../services/content';
import { openDownloadedContent } from '../../utils/openDownloadedContent';
import { Spin } from 'antd';
import clsx from 'clsx';
import './Document.scss';
import { isWordDocument } from '../../utils/getS3FileKey';
import { WordDocument } from './WordDocument';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const pageRenderOptions = {
  renderTextLayer: false,
  renderAnnotationLayer: false
} as const;

type ViewMode = 'vertical' | 'horizontal';

interface Props {
  canDownload: boolean
  contentId?: string
  fileKey: string
  name: string
  header?: ReactNode
}

const getDocumentLoadError = async (response: Response): Promise<string> => {
  if (response.status === 401) {
    return 'Sesión expirada. Vuelve a iniciar sesión.';
  }

  if (response.status === 404) {
    return 'Documento no encontrado.';
  }

  try {
    const body = await response.json() as { error?: string };
    if (body.error) {
      return body.error;
    }
  } catch {
    // Response body is not JSON (e.g. binary error page).
  }

  return 'No se pudo cargar el documento';
};

const DocumentRoot: FC<Props> = (props) => {
  const [usePdfFallback, setUsePdfFallback] = useState(false);

  if (isWordDocument(props.fileKey) && !usePdfFallback) {
    return (
      <WordDocument
        {...props}
        onFallbackToPdf={() => setUsePdfFallback(true)}
      />
    );
  }

  return <PdfDocumentViewer {...props} />;
};

const PdfDocumentViewer: FC<Props> = ({ canDownload, contentId, fileKey, name, header }) => {
  const { downloadContent, loading: downloading } = useDownloadContent();
  const viewerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>('vertical');
  const [scale, setScale] = useState(0.6);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const setPageRef = useCallback((page: number, node: HTMLDivElement | null) => {
    if (node) {
      pageRefs.current.set(page, node);
    } else {
      pageRefs.current.delete(page);
    }
  }, []);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setPdfUrl(null);

      try {
        const token = localStorage.getItem(AUTH_KEY) ?? '';
        const response = await fetch(
          `${BACKEND_URL}/documents/view/${encodeURIComponent(fileKey)}`,
          { headers: { accessToken: token } }
        );

        if (!response.ok) {
          throw new Error(await getDocumentLoadError(response));
        }

        const blob = await response.blob();
        if (cancelled) return;

        objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Error al cargar el documento');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fileKey]);

  useEffect(() => {
    if (viewMode !== 'vertical' || numPages === 0) return;

    const root = viewerRef.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visible) {
          const page = Number(visible.target.getAttribute('data-page'));
          if (!Number.isNaN(page)) {
            setCurrentPage(page);
          }
        }
      },
      {
        root,
        threshold: [0.2, 0.4, 0.6, 0.8]
      }
    );

    pageRefs.current.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [viewMode, numPages, scale]);

  const onDocumentLoadSuccess = useCallback(({ numPages: pages }: { numPages: number }) => {
    setNumPages(pages);
    setCurrentPage(1);
  }, []);

  const toggleViewMode = useCallback(() => {
    setViewMode((mode) => (mode === 'vertical' ? 'horizontal' : 'vertical'));
  }, []);

  const goToPreviousPage = useCallback(() => {
    setCurrentPage((page) => Math.max(1, page - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setCurrentPage((page) => Math.min(numPages, page + 1));
  }, [numPages]);

  const handleDownload = useCallback(async () => {
    if (!canDownload || !contentId) return;

    const response = await downloadContent(contentId);
    openDownloadedContent(response);
  }, [canDownload, contentId, downloadContent]);

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    if (!canDownload) {
      event.preventDefault();
    }
  }, [canDownload]);

  if (loading) {
    return (
      <div className="crossfactory-document__state">
        <Spin size="large" tip="Cargando documento..." />
      </div>
    );
  }

  if (error || !pdfUrl) {
    return (
      <div className="crossfactory-document__state">
        <p>{error ?? 'No se pudo cargar el documento'}</p>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'crossfactory-document',
        'notranslate',
        !canDownload && 'no-download',
        viewMode === 'horizontal' && 'crossfactory-document--horizontal'
      )}
      onContextMenu={handleContextMenu}
      translate="no"
    >
      <div className="crossfactory-document__header">
        {header}
        <div className="crossfactory-document__toolbar">
          {numPages > 0 && (
            <span className="crossfactory-document__page-info">
              Página {currentPage} de {numPages}
            </span>
          )}

          {viewMode === 'horizontal' && (
            <>
              <button
                type="button"
                className="crossfactory-document__btn cursor-pointer"
                onClick={goToPreviousPage}
                disabled={currentPage <= 1}
              >
                Anterior
              </button>
              <button
                type="button"
                className="crossfactory-document__btn cursor-pointer"
                onClick={goToNextPage}
                disabled={currentPage >= numPages}
              >
                Siguiente
              </button>
            </>
          )}

          <button
            type="button"
            className="crossfactory-document__btn crossfactory-document__mode-btn cursor-pointer"
            onClick={toggleViewMode}
            title={viewMode === 'vertical' ? 'Cambiar a modo horizontal' : 'Cambiar a modo vertical'}
          >
            {viewMode === 'vertical' ? (
              <>
                <IconLayoutNavbar size={16} />
                Horizontal
              </>
            ) : (
              <>
                <IconLayoutList size={16} />
                Vertical
              </>
            )}
          </button>

          <button
            type="button"
            className="crossfactory-document__btn cursor-pointer"
            onClick={() => setScale((s) => Math.max(0.4, Number((s - 0.1).toFixed(1))))}
          >
            −
          </button>
          <span className="crossfactory-document__page-info">{Math.round(scale * 100)}%</span>
          <button
            type="button"
            className="crossfactory-document__btn cursor-pointer"
            onClick={() => setScale((s) => Math.min(2, Number((s + 0.1).toFixed(1))))}
          >
            +
          </button>

          {canDownload && contentId && (
            <button
              type="button"
              className="crossfactory-document__btn crossfactory-document__download cursor-pointer"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? 'Descargando...' : 'Descargar'}
            </button>
          )}
        </div>
      </div>

      <div
        ref={viewerRef}
        className="crossfactory-document__viewer"
      >
        <PdfDocument file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess} loading={<Spin />}>
          {viewMode === 'vertical'
            ? Array.from({ length: numPages }, (_, index) => {
              const pageNumber = index + 1;
              return (
                <div
                  key={`page-${pageNumber}`}
                  ref={(node) => setPageRef(pageNumber, node)}
                  data-page={pageNumber}
                  className={clsx(
                    'crossfactory-document__page-wrapper',
                    currentPage === pageNumber && 'crossfactory-document__page-wrapper--active'
                  )}
                >
                  <Page pageNumber={pageNumber} scale={scale} {...pageRenderOptions} />
                </div>
              );
            })
            : (
              <div className="crossfactory-document__page-wrapper crossfactory-document__page-wrapper--active">
                <Page pageNumber={currentPage} scale={scale} {...pageRenderOptions} />
              </div>
            )}
        </PdfDocument>
      </div>
    </div>
  );
};

const Document = memo(DocumentRoot);

export { Document };
